import { Spinner } from "@blueprintjs/core";

import { Card } from "@/components/ui/card";
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { RoleplayRank, useRoleplaySessionsQuery } from "@/graphql";

import { SessionHistoryChartSection } from "./SessionHistoryChartSection";
import { SessionHistoryListSection } from "./SessionHistoryListSection";

interface SessionHistoryDrawerProps {
  scenarioId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SessionHistoryDrawer({
  scenarioId,
  open,
  onOpenChange,
}: SessionHistoryDrawerProps) {
  const { data, loading, error } = useRoleplaySessionsQuery({
    variables: {
      scenarioIds: [scenarioId],
    },
    skip: !open, // Don't fetch until drawer is opened
  });

  const sessions =
    data?.roleplaySessions?.map((session) => ({
      uuid: session?.uuid || "",
      updatedAt: session?.updatedAt || "",
      rank: session?.rank || RoleplayRank.Unranked,
      score: session?.score || 0,
      scenarioSlug: session?.scenario?.slug || "",
    })) ?? [];

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="w-3/4 sm:max-w-2xl">
        <DrawerCloseButton />
        <div className="flex flex-col h-full justify-between">
          <div className="flex flex-col overflow-y-auto">
            <div className="flex flex-col gap-6 p-6">
              <DrawerTitle className="text-xl">Your Sessions</DrawerTitle>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              ) : error ? (
                <Card className="p-8 text-center text-red-600">
                  <p className="text-lg">Error loading sessions</p>
                  <p className="text-sm mt-2">{error.message}</p>
                </Card>
              ) : (
                <>
                  <SessionHistoryChartSection sessions={sessions} />
                  <SessionHistoryListSection sessions={sessions} />
                </>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
